import {BehaviorSubject, Observable} from 'rxjs';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TrackByFunction,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {StylesService} from '../../styles/styles.service';
import {ActivityStyleKeeper} from '../../styles/style-keepers/activity-style';
import {NavigationService} from '../../../core/navigation/navigation.service';
import {ModeData, ModeDataService} from '../../../editor-state/mode-data.service';
import {GraphHelperService} from '../../helpers/graph-helper.service';
import {EditorRegistryService} from '../../mx-graph/editor/editor-registry.service';
import {DragulaService, Group, DragulaModule} from 'ng2-dragula';
import {ResourceCommandInput} from '../../../diagram-interaction/services/drag-assist.service';
import {ResourceCommands} from '../../../commands/resource/resource.commands';
import {UndoService} from '../../../commands/undo.service';
import {ActivatedRoute} from '@angular/router';
import {isComponent} from '../../helpers/shapes-detectors.util';
import {SidebarService} from '../../../editor-interface/sidebar/sidebar.service';
import {DomSanitizer, SafeStyle} from '@angular/platform-browser';
import {ClientEnvService} from '../../../core/services/client-env.service';
import {IActJson, IResourceJson} from '../../../diagram-data/diagram-json-handler.service';
import {InfoStyle, ResourceStyle} from '../../styles/styles.model';
import {AuthService} from '../../../core/auth/auth.service';
import {filterAttachmentsUtil, someAttachmentsPresent} from '../../helpers/attachments.util';
import * as _ from 'lodash';
import {environment} from 'src/environments/environment';
import {
  IToolbarMode,
  ToolbarStateService
} from '../../../editor-interface/toolbar/toolbar-state.service';
import {RESOURCE_TYPES} from '../../shape-spawn-data/resources.model';
import {MapService} from '../../../core/map/map.service';
import {
  DRAGULA_BAG_OPTIONS,
  DRAGULA_BAG_PREFIXES,
  DragElement
} from 'src/app/shared/models/dragula.interface';
import {DragulaConfigurationService} from 'src/app/shared/service/dragula-configuration.service';
import {ResourceTypePipe} from '../../pipes/resource-type.pipe';
import {HiddenResourceFilterPipe} from '../../helpers/hidden-resource-filter.pipe';
import {SanitizerPipe} from '../../../core/helpers/sanitizer.pipe';
import {TranslateModule} from '@ngx-translate/core';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ActivityPaperclipComponent} from './paper-clip/activity-paperclip.component';
import {OrgModelLinkComponent} from './org-model-link/org-model-link.component';
import {MatIconModule} from '@angular/material/icon';
import {CommonModule, KeyValue} from '@angular/common';
import {MxCell} from 'src/app/editor-interface/shared/models/mx-cell.interface';
import {ScreenshotPopoverDirective} from 'src/app/editor-interface/shared/directives/screenshot-popover.directive';
import {Q9HtmlDecoderPipe, Q9HtmlEncoderPipe} from '@q9elements/ui-kit/common';
import {DiagramTypeConfigService} from '../../../core/services/diagram-type-config.service';

declare const mxClient: any;
declare const mxEvent: any;

enum DRILLDOWN_ARCS {
  ARC_8 = 'drilldownArc8',
  ARC_4 = 'drilldownArc4'
}

type TrackById = `${'res-line'}:${string}`;

@Component({
  selector: 'app-activity-shape',
  templateUrl: './activity-shape.component.html',
  styleUrls: ['./activity-shape.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ScreenshotPopoverDirective,
    MatIconModule,
    OrgModelLinkComponent,
    ActivityPaperclipComponent,
    MatTooltipModule,
    DragulaModule,
    TranslateModule,
    SanitizerPipe,
    HiddenResourceFilterPipe,
    ResourceTypePipe,
    CommonModule,
    Q9HtmlDecoderPipe,
    Q9HtmlEncoderPipe
  ]
})
export class ActivityShapeComponent implements OnInit, OnDestroy {
  @ViewChild('resContainerRef', {read: ElementRef})
  public resContainerRef: ElementRef<HTMLDivElement>;

  @ViewChild('shadowHolder', {read: ElementRef})
  public shadowHolder: ElementRef<HTMLDivElement>;

  private bag!: Group;
  public readonly dragulaBagPrefix = DRAGULA_BAG_PREFIXES.ACTIVITY;

  public activity = true;
  public number: string | number;
  public mxKey: number;
  public source: IActJson;
  public isArchitecture = this.diagramTypeConfigService.isArchitecture;
  public isAgentInteraction = this.diagramTypeConfigService.isAgentInteraction;
  public readonly isSFA: Observable<boolean>;
  public fontWrapper: any;
  public fontStyle: any;
  public colors: any;
  public fillColor;
  public hiddenNumber: boolean;
  public icon: DRILLDOWN_ARCS;
  public readonly mode: Observable<ModeData>;
  public imageMode: BehaviorSubject<boolean> = new BehaviorSubject(void 0);
  public isEditing: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public textSub = new BehaviorSubject(this.diagramTypeConfigService.activitySettings().text);
  private styles: ActivityStyleKeeper;
  public allowDrop: boolean | undefined;
  public captureMode: boolean;
  public captureDelay: NodeJS.Timeout /** setTimeout has a number signature **/;
  public attributes: Array<any> = [];
  public resources: IResourceJson[] = [];
  public resourceLines!: Map<TrackById, IResourceJson[]>;
  public resourcesPerRow: number;
  public mainResource: IResourceJson;
  public resStyle: ResourceStyle;
  public infoStyle: InfoStyle;
  public showPaperclip = false;
  public borderWidth: string;
  public toolbarState$: Observable<IToolbarMode> = this.toolbarStateService.toolbarState$;
  public attributePadding: boolean;
  public trackByResourceLine = this.createResourcesLineTrackByFn();
  public trackByResource = this.createResourceTrackByFn();

  /** Empty array needed for proper dragula model init **/

  constructor(
    private elRef: ElementRef,
    private stylesSvc: StylesService,
    private navSvc: NavigationService,
    private modeDataSvc: ModeDataService,
    private graphHelperSvc: GraphHelperService,
    private mapService: MapService,
    private registry: EditorRegistryService,
    private dragulaSvc: DragulaService,
    private dragulaConfigurationService: DragulaConfigurationService,
    private cd: ChangeDetectorRef,
    private undoSvc: UndoService,
    private resCommands: ResourceCommands,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private sidebarSvc: SidebarService,
    private clientEnvSvc: ClientEnvService,
    private authSvc: AuthService,
    private toolbarStateService: ToolbarStateService,
    private diagramTypeConfigService: DiagramTypeConfigService
  ) {
    this.mode = modeDataSvc.currentMode$;
    this.isSFA = this.mapService.isSFA$;
  }

  ngOnInit(): void {
    this.initDragulaBag();
  }

  ngOnDestroy() {
    this.cd.detach();

    if (this.bag && this.resContainerRef) {
      const drake: any = this.bag.drake || {};
      const containers: Element[] = drake.containers || [];
      const models: any[][] = drake.models || [];
      const cIndex = containers.indexOf(this.resContainerRef.nativeElement);

      if (cIndex > -1) {
        containers.splice(cIndex, 1);
      }

      const mIndex = models.indexOf(this.resources);

      if (mIndex > -1) {
        models.splice(mIndex, 1);
      }
    }
  }

  private initDragulaBag(): void {
    const options = DRAGULA_BAG_OPTIONS[this.dragulaBagPrefix];

    this.bag = this.dragulaConfigurationService.createGroup({
      groupName: this.dragulaBagPrefix,
      ...options
    });
  }

  log(message: string) {
    console.log(message);
  }

  public safeImageUrl(url): SafeStyle {
    // if (!this.source.imageUrl) {
    if (!url) {
      return;
    }

    return this.sanitizer.bypassSecurityTrustStyle(`url(${url})`);
  }

  track(index, item) {
    if (item) {
      return index + item.resourceDbId;
    }

    return index;
  }

  private createResourcesLineTrackByFn(): TrackByFunction<KeyValue<TrackById, IResourceJson[]>> {
    return (_, item) => {
      return item.key;
    };
  }

  private createResourceTrackByFn(): TrackByFunction<IResourceJson> {
    return (index, item) => {
      if (item) {
        return 1 + item.resourceDbId;
      }

      return index;
    };
  }

  public setupFromJson(json?: IActJson): void {
    let cell: MxCell;

    if (!json) {
      cell = this.getCell();

      if (!cell) {
        return;
      }

      json = cell.getJson();
    }

    this.source = json;
    this.attributes = [...(json.attributes || [])];
    this.attributePadding = json.attributePadding ?? false;
    this.resources = json.resources ? [].concat(json.resources) : [];
    this.mainResource = this.resources.find(r => r.main);
    this.resourcesPerRow = json.resourcesPerRow ?? 0;
    this.resourceLines = this.generateResourceLines();
    this.styles = this.stylesSvc.activity(this.source);
    this.borderWidth = this.styles.boundingBox().borderWidth;
    this.number = this.source.number;
    this.textSub.next(this.source.text);
    this.setResourceStyle();
    this.setInfoStyle();
    this.setDrilldownStyle();
    this.setFontStyle();
    this.setImageMode();

    if (cell && isComponent(cell)) {
      cell.comp.instance.refreshParentStyle(cell.div);
    }

    this.showPaperclip = this.checkPaperClip(json);
    this.runChangeDetection();
  }

  public drillDown() {
    if (this.source.drilldowns || this.source.linkedDiagram) {
      const id = this.source.linkedDiagram ?? this.source.drilldowns[0].targetDbId;

      this.navSvc.relative(id);
    }
  }

  get configForDrilldownScreenshot() {
    if ((this.source.drilldowns || this.source.linkedDiagram) && this.navSvc.getCurrent()) {
      return {
        id: this.source.linkedDiagram ?? this.source.drilldowns[0].targetDbId,
        state: this.navSvc.getCurrent().state,
        size: this.clientEnvSvc.isStripped ? '_300x200' : '_600x400'
      };
    }
  }

  public toDrilldownInView() {
    if (this.modeDataSvc.isViewMode()) {
      this.drillDown();
    }
  }

  public transparencyLevel() {
    const styles: any = this.styles ? this.styles.boundingBox() : {borderWidth: '3px'};
    const styleObj = {
      // position: 'relative',
      bottom: '0px',
      'z-index': '3',
      width: `calc(100% - ${parseInt(styles.borderWidth, 10) * 2}px)`,
      opacity: '1'
    };

    return styleObj;
  }

  public refreshParentStyle(node?) {
    if (!node) {
      const cell = this.getCell();

      if (!cell) {
        return;
      }

      node = cell.div;
    }

    const styleObj: any = this.styles ? this.styles.boundingBox() : {};

    node.style.display = 'block';
    node.style.borderColor = styleObj.borderColor;
    node.style.borderStyle = styleObj.borderStyle;

    if (this.isArchitecture()) {
      this.shadowHolder.nativeElement.style.boxShadow = styleObj.boxShadow;
      this.shadowHolder.nativeElement.style.borderRadius = styleObj.borderRadius;
    } else {
      node.style.boxShadow = styleObj.boxShadow;
    }

    node.style.borderRadius = styleObj.borderRadius;
    node.style.borderWidth = styleObj.borderWidth;
    node.style.backgroundColor = styleObj.backgroundColor;
  }

  private getCell(): MxCell {
    if (this.mxKey) {
      return this.graphHelperSvc.allActivities().find(act => Number(act.id) === Number(this.mxKey));
    }
  }

  private setImageMode(force?: boolean) {
    if (force !== void 0) {
      this.imageMode.next(force);
    } else {
      this.imageMode.next(Boolean(this.source.image && this.source.imageUrl));
    }
  }

  private setFontStyle() {
    this.fontWrapper = this.styles.fontWrapper();
    this.fontStyle = this.styles.fontCss();
  }

  public setResourceStyle() {
    this.resStyle = this.styles.getResourceStyle();
  }

  public setInfoStyle() {
    this.infoStyle = this.styles.getInfoStyle();
  }

  private setDrilldownStyle() {
    const base = this.styles.colors();
    const shape = this.styles.shape();

    if (this.source.drilldowns || this.source.linkedDiagram) {
      this.colors = {
        color: base.numberIconcolor,
        'border-left-color': base.borderColor,
        fill: base.borderColor
      };
    } else {
      this.colors = {
        color: base.numberColor
      };
    }

    this.hiddenNumber = shape.hideNumber;
    this.icon = this.isArchitecture() ? DRILLDOWN_ARCS.ARC_8 : DRILLDOWN_ARCS.ARC_4;
  }

  private generateResourceLines(): Map<TrackById, IResourceJson[]> {
    const map = new Map<TrackById, IResourceJson[]>();

    if (!this.resources.length) {
      return map;
    }

    for (let i = 0; i < this.resources.length; i++) {
      const resource = this.resources[i];

      if (resource.main || resource.hidden) {
        continue;
      }

      const lineIdx = this.resourcesPerRow === 0 ? 0 : Math.floor(i / this.resourcesPerRow);
      const lineId = `res-line:${lineIdx}` as const;
      const line = map.get(lineId) ?? [];

      line.push(resource);
      map.set(lineId, line);
    }

    return map;
  }

  public overloadWithMetadata(evt, resource, index) {
    if (this.modeDataSvc.isViewMode() || evt.isCloned) {
      return;
    }

    let dragElement: DragElement;
    const dragMetadata: ResourceCommandInput = {
      resource,
      srcId: this.source.mxKey,
      srcPos: index
    };
    const dragShadow: any = {};
    const resStyling: any = {};
    const actStyling: any = {};

    const path = evt.composedPath ? evt.composedPath() : findEventPath(evt);

    path
      .filter(el => !!el.classList)
      .forEach(el => {
        if (el.classList.contains('resource')) {
          dragElement = el;
        } else if (el.classList.contains('list-group')) {
          /** Copy-paste resource and activity inline styles **/
          Object.assign(resStyling, getStyleMapFromEl(el));
        } else if (el.nodeName === 'APP-ACTIVITY-SHAPE') {
          if (el.parentNode && el.parentNode instanceof HTMLElement) {
            Object.assign(actStyling, getStyleMapFromEl(el.parentNode));
            delete actStyling.width;
            delete actStyling.height;
          }

          Object.assign(actStyling, getStyleMapFromEl(el));
        }
      });
    Object.assign(dragShadow, actStyling, resStyling);
    delete dragShadow.left;
    delete dragShadow.top;
    dragElement.dataset.dragMetadata = JSON.stringify(dragMetadata);
    dragElement.dataset.dragShadow = JSON.stringify(dragShadow);
    dragElement.dataset.isCopied = String(
      Number(mxClient.IS_MAC ? mxEvent.isMetaDown(evt) : mxEvent.isControlDown(evt))
    ) as '1' | '0';

    if (evt.metaKey || evt.ctrlKey) {
      evt.stopImmediatePropagation();
      evt.preventDefault();
      /** because dragula ignores events with meta key**/
      let clone;
      const cfg = {
        bubbles: evt.bubbles,
        cancelable: evt.cancelable,
        screenX: evt.screenX,
        screenY: evt.screenY,
        clientX: evt.clientX,
        clientY: evt.clientY,
        shiftKey: evt.shiftKey,
        altKey: evt.altKey,
        button: evt.button,
        buttons: evt.buttons,
        relatedTarget: evt.relatedTarget
      };

      if (mxClient.IS_IE11) {
        // TODO: find out why event bubbling order in IE is different (also fix this)
        clone = document.createEvent('MouseEvents');
        clone.initMouseEvent(
          'mousedown',
          cfg.bubbles,
          cfg.cancelable,
          window,
          null,
          cfg.screenX,
          cfg.screenY,
          cfg.clientX,
          cfg.clientY,
          null,
          cfg.altKey,
          cfg.shiftKey,
          null,
          cfg.button,
          null
        );
      } else {
        clone = new MouseEvent('mousedown', cfg);
      }

      clone['isCloned'] = true;
      evt.target.dispatchEvent(clone);
    }
  }

  public markDropArea(resourceId: string, actMxKey: number, copy?: boolean) {
    if (!this.source.resources) {
      this.source.resources = [];
    }

    if (this.source.mxKey === actMxKey) {
      this.allowDrop = !this.isArchitecture() && !copy;
    } else {
      const existing = this.source.resources.find(res => {
        return String(res.id || res.resourceDbId) === resourceId;
      });

      this.allowDrop = !existing;
    }

    this.captureDelay = setTimeout(() => {
      // prevent dragged resource from getting desynced from mouse position by delaying capture mode
      this.captureMode = true;
      this.runChangeDetection();
    });
    this.runChangeDetection();
  }

  public clearDropArea() {
    this.allowDrop = void 0;

    if (this.captureDelay) {
      clearTimeout(this.captureDelay);
    }

    this.captureMode = false;
    this.runChangeDetection();
  }

  public runChangeDetection() {
    this.cd.detectChanges();
  }

  public detachResource(resource, index) {
    const commandData = {
      resource,
      position: index + 1,
      activityMxKey: this.source.mxKey
    };
    const command = this.resCommands.detach(commandData);

    this.undoSvc.executeCommand(command);
  }

  public highlight() {
    const cell = this.getCell();

    if (cell && cell.div) {
      cell.div.parentElement?.classList?.add('shadow-pulse');
      cell.div.parentElement?.addEventListener('animationend', () => {
        cell.div.parentElement?.classList?.remove('shadow-pulse');
      });

      cell.div.classList.add('shadow-pulse');
      cell.div.addEventListener('animationend', () => {
        cell.div.classList.remove('shadow-pulse');
      });
    }
  }

  public toggleHighlight(isActive: boolean): void {
    const cell = this.getCell();

    if (cell && cell.div) {
      if (isActive) {
        return cell.div.classList?.add('activity-highlight');
      }

      return cell.div.classList?.remove('activity-highlight');
    }
  }

  public checkPaperClip(json): boolean {
    const isViewMode = this.modeDataSvc.isViewMode();
    const userData = this.authSvc.getUserDataSync();
    const userGroups = _.get(userData, 'userInTeam.groups');
    const baseAtm = filterAttachmentsUtil(json.attachments, userGroups);
    const reqAtm = filterAttachmentsUtil(json.requirementAttachments, userGroups);
    const storyAtm = filterAttachmentsUtil(json.storyAttachments, userGroups);
    const refModelAtm = filterAttachmentsUtil(json.refModelAttachments, userGroups);
    const filteredAtm = [...baseAtm, ...refModelAtm, ...reqAtm, ...storyAtm];

    if (environment.NOUI) {
      const concat =
        (array = []) =>
        (nextArray = []) => [...array, ...nextArray];
      const attachments = concat(json.attachments)(json.refModelAttachments);

      return !!attachments.length;
    }

    return isViewMode ? !!filteredAtm.length : someAttachmentsPresent(json);
  }

  public getResourceStyle(resourceType) {
    return this.resStyle[RESOURCE_TYPES[resourceType]];
  }
}

function getStyleMapFromEl(el: HTMLElement) {
  /** converts CSSStyleDeclaration objects into a key:value map **/
  return Array.from(el.style).reduce((acc, curr) => {
    acc[curr] = el.style[curr];

    return acc;
  }, {});
}

// a very lackluster implementation of event.path for IE
function findEventPath(evt: MouseEvent) {
  if (!evt.target) {
    return [];
  }

  return flattenParents(evt.target as HTMLElement);
}

function flattenParents(e: HTMLElement, acc?: Array<HTMLElement>): Array<HTMLElement> {
  const accum = acc || [];

  accum.push(e);

  if (e.parentElement) {
    return flattenParents(e.parentElement, accum);
  } else {
    return accum;
  }
}
